import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import type { Event, LeagueStandings } from "@liga/shared";
import { api } from "../api/client";
import { ErrorBox, Loading } from "../components/Loading";
import { usePageTitle } from "../hooks/usePageTitle";

/** YYYY-MM portion of an ISO date string. */
function monthKeyOf(isoDate: string): string {
  return isoDate.slice(0, 7);
}

/** "abr 2026" given "2026-04". */
function formatMonthLabel(monthKey: string): string {
  const [year, monthStr] = monthKey.split("-");
  const monthNum = Number(monthStr);
  const dt = new Date(Date.UTC(Number(year), monthNum - 1, 1));
  const label = new Intl.DateTimeFormat("pt-BR", {
    month: "short",
    timeZone: "UTC",
  }).format(dt);
  return `${label.replace(/\.$/, "")} ${year}`;
}

/** "02/04" given "2026-04-02". */
function formatDayLabel(isoDate: string): string {
  const [, month, day] = isoDate.split("-");
  return `${day}/${month}`;
}

interface ColumnGroup {
  monthKey: string;
  label: string;
  events: Event[];
  /** Whether this month qualifies for the per-month worst-event drop. */
  hasDrop: boolean;
}

/**
 * Build the per-month grouping the matrix renders, including a flag for
 * whether the month qualifies for a drop. A month qualifies when:
 *   - the league spans ≥ 2 months, AND
 *   - the month has ≥ 2 events in the league, AND
 *   - the league either has no per-month plan, or the month's event count
 *     reached the planned count (mirrors backend `computeStandings`).
 */
function buildColumnGroups(standings: LeagueStandings): ColumnGroup[] {
  const { league, events } = standings;
  const sortedEvents = [...events].sort((a, b) => a.date.localeCompare(b.date));

  // Plan map: YYYY-MM -> expected number of events. Replicates
  // `expectedEventsByMonth` from the backend so we apply the same gating to
  // the "Pior <Mês>" columns.
  const planByMonth = new Map<string, number>();
  if (league.etapasPerMonth) {
    const startYear = Number(league.startDate.slice(0, 4));
    const startMonth = Number(league.startDate.slice(5, 7));
    league.etapasPerMonth.forEach((expected, i) => {
      const idx = startMonth - 1 + i;
      const year = startYear + Math.floor(idx / 12);
      const month = (idx % 12) + 1;
      const key = `${year}-${String(month).padStart(2, "0")}`;
      planByMonth.set(key, expected);
    });
  }

  const grouped = new Map<string, Event[]>();
  for (const e of sortedEvents) {
    const key = monthKeyOf(e.date);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(e);
    else grouped.set(key, [e]);
  }

  const multiMonth = league.durationMonths >= 2;
  return Array.from(grouped.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthKey, monthEvents]) => {
      const expected = planByMonth.get(monthKey);
      const monthIsFull = expected === undefined || monthEvents.length >= expected;
      const hasDrop = multiMonth && monthEvents.length >= 2 && monthIsFull;
      return {
        monthKey,
        label: formatMonthLabel(monthKey),
        events: monthEvents,
        hasDrop,
      };
    });
}

function medalClass(pos: number): string {
  if (pos === 1) return "bg-[#d4af37] text-white";
  if (pos === 2) return "bg-[#c3c7cf] text-white";
  if (pos === 3) return "bg-[#c0894e] text-white";
  return "bg-[#efece6] text-[#7a7263]";
}

export function LeagueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const leagueId = Number(id);

  const standingsQuery = useQuery({
    queryKey: ["standings", leagueId],
    queryFn: () => api.leagueStandings(leagueId),
    enabled: Number.isFinite(leagueId),
  });

  const standings = standingsQuery.data;
  usePageTitle(standings ? standings.league.name : null);

  const columnGroups = useMemo(
    () => (standings ? buildColumnGroups(standings) : []),
    [standings],
  );
  const dropMonths = useMemo(
    () => columnGroups.filter((g) => g.hasDrop),
    [columnGroups],
  );

  if (!Number.isFinite(leagueId)) {
    return <ErrorBox error={new Error("Liga inválida")} />;
  }
  if (standingsQuery.isLoading) return <Loading />;
  if (standingsQuery.error) return <ErrorBox error={standingsQuery.error} />;
  if (!standings) return <ErrorBox error={new Error("Liga não encontrada")} />;

  const { league, rows } = standings;
  const monthsLabel =
    league.durationMonths === 1 ? "Mês único" : `${league.durationMonths} meses`;
  const etapasLabel = league.etapasPerMonth
    ? `${league.etapasPerMonth.reduce((s, n) => s + n, 0)} etapas (${league.etapasPerMonth.join("+")})`
    : null;

  // Total events per column group (used to compute table colspans).
  const totalEventCols = columnGroups.reduce((s, g) => s + g.events.length, 0);
  const totalsColCount = 1 /* Total */ + dropMonths.length + 1 /* Resultado Liga */ + 2; /* Part + AVG */

  return (
    <div className="space-y-6">
      <header>
        <Link
          to="/leagues"
          className="text-sm font-medium text-brand-700 hover:underline"
        >
          ← Voltar para ligas
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold mb-1.5">
              Liga
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-ink">
              {league.name}
            </h1>
            <p className="text-sm text-[#8a8170] mt-1">
              {league.startDate} → {league.endDate} · {monthsLabel}
              {etapasLabel ? ` · ${etapasLabel}` : ""}
            </p>
          </div>
        </div>
      </header>

      {league.durationMonths >= 2 && (
        <p className="max-w-[760px] text-[14px] leading-relaxed text-[#6b6356]">
          Cada célula mostra os pontos do jogador na etapa (com o{" "}
          <strong>+1 de participação</strong>) — falta vale <strong>0</strong>.
          Em cada mês com ≥ 2 etapas, o <strong>pior resultado</strong> é
          descontado do total (destacado em amarelo). Quando o pior do mês foi
          uma falta, o valor descontado é <code>0</code> e nenhuma etapa fica
          destacada.
        </p>
      )}

      {rows.length === 0 ? (
        <div className="rounded-2xl border border-line bg-white p-8 text-center text-slate-500">
          Sem resultados para esta liga ainda.
        </div>
      ) : (
        <div className="rounded-2xl border border-line bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-brand-50 text-brand-900 text-[11px] uppercase tracking-wide">
                  <th
                    rowSpan={2}
                    className="sticky left-0 z-20 bg-brand-50 px-3 py-2 text-left font-bold w-12"
                  >
                    #
                  </th>
                  <th
                    rowSpan={2}
                    className="sticky left-12 z-20 bg-brand-50 px-3 py-2 text-left font-bold min-w-[180px] border-r border-brand-100"
                  >
                    Jogador
                  </th>
                  {columnGroups.map((g) => (
                    <th
                      key={g.monthKey}
                      colSpan={g.events.length}
                      className="px-2 py-2 text-center font-bold border-l border-brand-100"
                    >
                      {g.label}
                    </th>
                  ))}
                  <th
                    colSpan={totalsColCount}
                    className="px-2 py-2 text-center font-bold border-l-2 border-brand-300"
                  >
                    Totais
                  </th>
                </tr>
                <tr className="bg-brand-50 text-brand-900 text-[11px] uppercase tracking-wide border-t border-brand-100">
                  {columnGroups.flatMap((g, gi) =>
                    g.events.map((ev, ei) => (
                      <th
                        key={ev.id}
                        className={[
                          "px-2 py-2 text-center font-semibold w-[58px]",
                          ei === 0 ? "border-l border-brand-100" : "",
                          gi === columnGroups.length - 1 &&
                          ei === g.events.length - 1
                            ? ""
                            : "",
                        ].join(" ")}
                      >
                        <Link
                          to={`/events/${ev.id}`}
                          title={ev.name}
                          className="hover:underline decoration-dotted underline-offset-2"
                        >
                          {formatDayLabel(ev.date)}
                        </Link>
                      </th>
                    )),
                  )}
                  <th className="px-2 py-2 text-right font-bold border-l-2 border-brand-300 w-[64px]">
                    Total
                  </th>
                  {dropMonths.map((g) => (
                    <th
                      key={`pior-${g.monthKey}`}
                      className="px-2 py-2 text-right font-bold border-l border-brand-100 w-[70px]"
                      title={`Pior resultado de ${g.label}`}
                    >
                      Pior {g.label.split(" ")[0]}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-right font-bold border-l border-brand-100 w-[80px]">
                    Resultado
                  </th>
                  <th className="px-2 py-2 text-right font-bold border-l border-brand-100 w-[64px]">
                    Part.
                  </th>
                  <th className="px-2 py-2 text-right font-bold border-l border-brand-100 w-[70px]">
                    AVG Rank
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const pos = idx + 1;
                  const top = pos <= 3;
                  const resultsByEvent = new Map(
                    row.results.map((r) => [r.eventId, r]),
                  );
                  const droppedSet = new Set(row.droppedEventIds);
                  // Zeros (DNPs) that consumed a month's drop slot get the
                  // same amber highlight as attended drops — the user can
                  // see at a glance which 0 was descartado.
                  const droppedDnpSet = new Set(row.droppedDnpEventIds);

                  // Compute "Pior" per qualifying month: the value of the
                  // dropped event (raw + 1) if attended, else 0 (DNP was
                  // the worst of the month). Total sums (raw + 1) over every
                  // attended event — that's the "Total" before any drop.
                  const piorByMonth = new Map<string, number>();
                  for (const g of dropMonths) {
                    let pior = 0;
                    let foundAttended = false;
                    for (const ev of g.events) {
                      if (droppedSet.has(ev.id)) {
                        const r = resultsByEvent.get(ev.id);
                        if (r) {
                          pior = r.points + 1;
                          foundAttended = true;
                        }
                        break;
                      }
                    }
                    if (!foundAttended) pior = 0;
                    piorByMonth.set(g.monthKey, pior);
                  }

                  const total = row.results.reduce(
                    (s, r) => s + r.points + 1,
                    0,
                  );

                  const rowBg = top ? "bg-[#fcf9f2]" : "bg-white";
                  return (
                    <tr
                      key={row.playerId}
                      className={`${rowBg} border-t border-[#f3efe6]`}
                    >
                      <td
                        className={`sticky left-0 z-10 ${rowBg} px-3 py-2 align-middle`}
                      >
                        <span
                          className={[
                            "inline-flex items-center justify-center min-w-[26px] h-[26px] rounded-lg text-[12px] font-bold font-display",
                            medalClass(pos),
                          ].join(" ")}
                        >
                          {pos}
                        </span>
                      </td>
                      <td
                        className={`sticky left-12 z-10 ${rowBg} px-3 py-2 align-middle min-w-[180px] border-r border-[#f3efe6]`}
                      >
                        <span
                          className={[
                            "text-[14px]",
                            top ? "font-bold text-ink" : "font-medium text-ink",
                          ].join(" ")}
                        >
                          {row.playerName}
                        </span>
                      </td>
                      {columnGroups.flatMap((g) =>
                        g.events.map((ev, ei) => {
                          const r = resultsByEvent.get(ev.id);
                          const isAttendedDrop = droppedSet.has(ev.id);
                          const isDnpDrop = droppedDnpSet.has(ev.id);
                          const isDropped = isAttendedDrop || isDnpDrop;
                          const value = r ? r.points + 1 : 0;
                          return (
                            <td
                              key={ev.id}
                              className={[
                                "px-2 py-2 text-center tabular-nums",
                                ei === 0 ? "border-l border-[#f3efe6]" : "",
                                isDropped
                                  ? "bg-amber-200/70 text-amber-900 font-bold"
                                  : r
                                    ? "text-ink"
                                    : "text-[#c4baa6]",
                              ].join(" ")}
                              title={
                                r
                                  ? `${ev.name} · #${r.rank} · ${r.points} pts (+1 part.)${isAttendedDrop ? " · descartado" : ""}`
                                  : `${ev.name} · não participou${isDnpDrop ? " · descartado (pior do mês)" : ""}`
                              }
                            >
                              {value}
                            </td>
                          );
                        }),
                      )}
                      <td className="px-2 py-2 text-right tabular-nums font-semibold text-ink border-l-2 border-brand-200">
                        {total}
                      </td>
                      {dropMonths.map((g) => (
                        <td
                          key={`pior-${row.playerId}-${g.monthKey}`}
                          className="px-2 py-2 text-right tabular-nums text-[#6b6356] border-l border-[#f3efe6]"
                        >
                          {piorByMonth.get(g.monthKey) ?? 0}
                        </td>
                      ))}
                      <td className="px-2 py-2 text-right tabular-nums font-bold font-display text-ink border-l border-[#f3efe6]">
                        {row.points}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-[#6b6356] border-l border-[#f3efe6]">
                        {row.eventsPlayed}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-[#6b6356] border-l border-[#f3efe6]">
                        {row.avgRank.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-[#efeae0] px-4 py-2.5 text-[11px] text-[#8a8170] flex items-center justify-between gap-3">
            <span>
              {rows.length} {rows.length === 1 ? "jogador" : "jogadores"} ·{" "}
              {totalEventCols} {totalEventCols === 1 ? "etapa" : "etapas"}
            </span>
            {dropMonths.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-amber-200/70 border border-amber-300" />
                Etapa descartada (pior do mês)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
